import { CheckedAddress, NormalizedNumber, raise } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { Address } from 'viem'
import { Config, useConfig } from 'wagmi'

import { getFarmContractData } from './getFarmContractData'
import { getChainConfigEntry } from '@/config/chain'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { Token } from '@/domain/types/Token'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'

export interface UseSpkFarmDataParams {
  chainId: number
  tokenRepository: TokenRepository
}

export interface UseSpkFarmDataResult {
  farmAddress: CheckedAddress
  rewardToken: Token
  stakingToken: Token
  rewardRate: NormalizedNumber
  earnedTimestamp: number
  periodFinish: number
  totalSupply: NormalizedNumber
  earned: NormalizedNumber
  staked: NormalizedNumber
}

export function useSpkFarmData({ chainId, tokenRepository }: UseSpkFarmDataParams): UseSpkFarmDataResult {
  const wagmiConfig = useConfig()
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { data } = useSuspenseQuery(spkFarmDataQueryOptions({ tokenRepository, wagmiConfig, chainId, account }))

  return data
}

interface SpkFarmDataQueryOptionsParams {
  tokenRepository: TokenRepository
  wagmiConfig: Config
  chainId: number
  account: CheckedAddress | undefined
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function spkFarmDataQueryOptions({ tokenRepository, wagmiConfig, chainId, account }: SpkFarmDataQueryOptionsParams) {
  const chainConfig = getChainConfigEntry(chainId)
  const farmAddress = chainConfig.spk?.farmConfig.address ?? raise('SPK farm address is not defined on this chain')

  return queryOptions({
    queryKey: spkFarmDataQueryKey({ chainId, account }),
    queryFn: () => getFarmContractData({ farmAddress, tokenRepository, wagmiConfig, chainId, account }),
  })
}

export function spkFarmDataQueryKey({ chainId, account }: { chainId: number; account: Address | undefined }): QueryKey {
  return ['spkFarmData', chainId, account]
}
